import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, exhaustMap, filter, mergeMap, share, switchMap, withLatestFrom } from 'rxjs';
import { errMessage } from '@ui';
import { ContactGroupsActions } from '../state/contact-groups/contact-groups.actions';
import { contactGroupsFeature } from '../state/contact-groups/contact-groups.feature';
import { ContactGroupsService } from '../../services/contact-groups.service';
import { SharedActions } from '../state/shared/shared.actions';

export const startLoadingContactGroups$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startLoadingContactGroups),
      withLatestFrom(
        store.select(contactGroupsFeature.selectListLoaded),
        store.select(contactGroupsFeature.selectIsLoading),
      ),
      filter(([, listLoaded, isLoading]) => !listLoaded && !isLoading),
      exhaustMap(() => [
        ContactGroupsActions.setIsLoading({ isLoading: true }),
        ContactGroupsActions.loadContactGroups(),
      ]),
    ),
  { functional: true },
);

export const loadContactGroups$ = createEffect(
  (actions$ = inject(Actions), service = inject(ContactGroupsService)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.loadContactGroups),
      switchMap(() =>
        service.getContactGroups().pipe(
          switchMap((contactGroups) => [
            ContactGroupsActions.loadContactGroupsSuccess({ contactGroups }),
            ContactGroupsActions.setIsLoading({ isLoading: false }),
          ]),
          catchError((response: HttpErrorResponse) => [
            ContactGroupsActions.loadContactGroupsFail(),
            ContactGroupsActions.setIsLoading({ isLoading: false }),
            SharedActions.showMessage({ error: errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const startLoadingContactGroupMembers$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startLoadingContactGroupMembers),
      exhaustMap(({ groupId }) => [
        ContactGroupsActions.loadContactGroupMembers({ groupId }),
      ]),
    ),
  { functional: true },
);

export const loadContactGroupMembers$ = createEffect(
  (actions$ = inject(Actions), service = inject(ContactGroupsService)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.loadContactGroupMembers),
      switchMap(({ groupId }) =>
        service.getMembers(groupId).pipe(
          switchMap((response) => [
            ContactGroupsActions.loadContactGroupMembersSuccess({
              groupId: response.groupId,
              members: response.members,
            }),
          ]),
          catchError((response: HttpErrorResponse) => [
            ContactGroupsActions.loadContactGroupMembersFail({ groupId }),
            SharedActions.showMessage({ error: errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const startCreateContactGroup$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startCreateContactGroup),
      exhaustMap(({ create }) => [
        ContactGroupsActions.setIsSaving({ isSaving: true }),
        ContactGroupsActions.createContactGroup({ create }),
      ]),
      share(),
    ),
  { functional: true },
);

export const createContactGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactGroupsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ContactGroupsActions.createContactGroup),
      mergeMap(({ create }) =>
        service.create(create).pipe(
          switchMap((contactGroup) => [
            ContactGroupsActions.createContactGroupSuccess({ contactGroup }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.CONTACT_GROUP_CREATED') as string,
            }),
          ]),
          catchError((response: HttpErrorResponse) => [
            SharedActions.showMessage({ error: errMessage(response) }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            ContactGroupsActions.createContactGroupFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startUpdateContactGroup$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startUpdateContactGroup),
      exhaustMap(({ id, update }) => [
        ContactGroupsActions.addToProcessingID({ id }),
        ContactGroupsActions.setIsSaving({ isSaving: true }),
        ContactGroupsActions.updateContactGroup({ id, update }),
      ]),
      share(),
    ),
  { functional: true },
);

export const updateContactGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactGroupsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ContactGroupsActions.updateContactGroup),
      mergeMap(({ id, update }) =>
        service.update(id, update).pipe(
          switchMap((contactGroup) => [
            ContactGroupsActions.updateContactGroupSuccess({ contactGroup }),
            ContactGroupsActions.removeFromProcessingID({ id }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.CONTACT_GROUP_UPDATED') as string,
            }),
          ]),
          catchError((response: HttpErrorResponse) => [
            SharedActions.showMessage({ error: errMessage(response) }),
            ContactGroupsActions.removeFromProcessingID({ id }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            ContactGroupsActions.updateContactGroupFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startUpdateContactGroupMembers$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startUpdateContactGroupMembers),
      exhaustMap(({ id, update }) => [
        ContactGroupsActions.addToProcessingID({ id }),
        ContactGroupsActions.setIsSaving({ isSaving: true }),
        ContactGroupsActions.updateContactGroupMembers({ id, update }),
      ]),
      share(),
    ),
  { functional: true },
);

export const updateContactGroupMembers$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactGroupsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ContactGroupsActions.updateContactGroupMembers),
      mergeMap(({ id, update }) =>
        service.updateMembers(id, update).pipe(
          switchMap((contactGroup) => [
            ContactGroupsActions.updateContactGroupMembersSuccess({
              contactGroup,
            }),
            ContactGroupsActions.removeFromProcessingID({ id }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.CONTACT_GROUP_MEMBERS_UPDATED') as string,
            }),
            ContactGroupsActions.startLoadingContactGroupMembers({
              groupId: id,
            }),
          ]),
          catchError((response: HttpErrorResponse) => [
            SharedActions.showMessage({ error: errMessage(response) }),
            ContactGroupsActions.removeFromProcessingID({ id }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            ContactGroupsActions.updateContactGroupMembersFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startRemoveContactGroupMember$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startRemoveContactGroupMember),
      exhaustMap(({ groupId, memberId, memberType }) => [
        ContactGroupsActions.addToProcessingID({ id: groupId }),
        ContactGroupsActions.setIsSaving({ isSaving: true }),
        ContactGroupsActions.removeContactGroupMember({
          groupId,
          memberId,
          memberType,
        }),
      ]),
      share(),
    ),
  { functional: true },
);

export const removeContactGroupMember$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactGroupsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ContactGroupsActions.removeContactGroupMember),
      mergeMap(({ groupId, memberId, memberType }) =>
        service.removeMember(groupId, memberId, memberType).pipe(
          switchMap((contactGroup) => [
            ContactGroupsActions.removeContactGroupMemberSuccess({
              contactGroup,
            }),
            ContactGroupsActions.removeFromProcessingID({ id: groupId }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.CONTACT_GROUP_MEMBER_REMOVED') as string,
            }),
            ContactGroupsActions.startLoadingContactGroupMembers({ groupId }),
          ]),
          catchError((response: HttpErrorResponse) => [
            SharedActions.showMessage({ error: errMessage(response) }),
            ContactGroupsActions.removeFromProcessingID({ id: groupId }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            ContactGroupsActions.removeContactGroupMemberFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startDeleteContactGroup$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startDeleteContactGroup),
      exhaustMap(({ id }) => [
        ContactGroupsActions.addToProcessingID({ id }),
        ContactGroupsActions.deleteContactGroup({ id }),
      ]),
      share(),
    ),
  { functional: true },
);

export const deleteContactGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactGroupsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ContactGroupsActions.deleteContactGroup),
      mergeMap(({ id }) =>
        service.delete(id).pipe(
          switchMap(() => [
            ContactGroupsActions.deleteContactGroupSuccess({ id }),
            ContactGroupsActions.removeFromProcessingID({ id }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.CONTACT_GROUP_DELETED') as string,
            }),
          ]),
          catchError((response: HttpErrorResponse) => [
            SharedActions.showMessage({ error: errMessage(response) }),
            ContactGroupsActions.removeFromProcessingID({ id }),
            ContactGroupsActions.deleteContactGroupFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);
